import React, { useState, useEffect, useCallback } from 'react';
import { LogOut, Plus, Building2, Loader2, X, ChevronRight, CheckCircle, Users, UserPlus, AlertTriangle, Pencil, UserX, UserCheck, MailX, MailPlus, Activity, Lock } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
    apiClient,
    Organisation,
    OrgUser,
    AdminActivityEntry,
    CreateOrganisationRequest,
    InviteAdminRequest,
    OrgType,
    FleetSubtype,
    ConsentMethod,
    AuthResponse,
} from '../services/api';
import ResetPasswordModal from './Modals/ResetPasswordModal';
import SystemHealth from './SystemHealth';

type User = AuthResponse['user'];

interface SuperAdminPageProps {
    user: User;
    onLogout: () => void;
}

// ─── Shared constants ─────────────────────────────────────────────────────────

const ORG_TYPES: { value: OrgType; label: string }[] = [
    { value: 'fleet', label: 'Fleet' },
    { value: 'recruitment', label: 'Recruitment' },
    { value: 'compliance', label: 'Compliance' },
];

const FLEET_SUBTYPES: { value: FleetSubtype; label: string }[] = [
    { value: 'general', label: 'General' },
    { value: 'bus', label: 'Bus (PCV)' },
    { value: 'coach', label: 'Coach (PCV)' },
    { value: 'haulage', label: 'Haulage (HGV/LGV)' },
    { value: 'car_fleet', label: 'Car Fleet' },
];

const CONSENT_METHODS: { value: ConsentMethod; label: string; description: string }[] = [
    { value: 'email', label: 'Email', description: 'Send consent request by email. The driver clicks a link and signs online.' },
    { value: 'offline', label: 'Offline (D906 form)', description: 'Physical form. Admin records the reference and date manually.' },
    { value: 'external', label: 'External system', description: "Consent managed in the client's own CRM. Reference and date required on driver creation." },
];

const ORG_ROLES: { value: InviteAdminRequest['role']; label: string }[] = [
    { value: 'admin', label: 'Admin' },
    { value: 'manager', label: 'Manager' },
    { value: 'compliance', label: 'Compliance Officer' },
    { value: 'user', label: 'User' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getUserStatus(u: OrgUser): 'active' | 'pending' | 'expired' | 'inactive' {
    if (u.isActive) return 'active';
    if (u.invitationToken && u.invitationExpiry && new Date(u.invitationExpiry) > new Date()) return 'pending';
    if (u.invitationToken && u.invitationExpiry && new Date(u.invitationExpiry) <= new Date()) return 'expired';
    return 'inactive';
}

const STATUS_BADGE: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    expired: 'bg-orange-100 text-orange-700',
    inactive: 'bg-gray-100 text-gray-500',
};

const STATUS_LABEL: Record<string, string> = {
    active: 'Active',
    pending: 'Pending',
    expired: 'Expired',
    inactive: 'Inactive',
};

const ROLE_BADGE: Record<string, string> = {
    super_admin: 'bg-red-100 text-red-700',
    admin: 'bg-green-100 text-green-700',
    manager: 'bg-blue-100 text-blue-700',
    compliance: 'bg-purple-100 text-purple-700',
    user: 'bg-gray-100 text-gray-600',
};

const inputClass = (hasError: boolean) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${hasError ? 'border-red-500 bg-red-50' : 'border-gray-300'} focus:ring-green-400`;

// ─── Org Type Badge ───────────────────────────────────────────────────────────

const OrgTypeBadge: React.FC<{ type: string }> = ({ type }) => {
    const colours: Record<string, string> = {
        fleet: 'bg-blue-100 text-blue-700',
        recruitment: 'bg-purple-100 text-purple-700',
        compliance: 'bg-orange-100 text-orange-700',
    };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${colours[type] ?? 'bg-gray-100 text-gray-700'}`}>
            {type}
        </span>
    );
};

// ─── Org Detail Modal ─────────────────────────────────────────────────────────

type DetailTab = 'details' | 'users' | 'danger';

interface OrgDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    org: Organisation | null;
    onOrgUpdated: (org: Organisation) => void;
    onOrgDeleted: (orgId: string) => void;
}

const emptyInvite: InviteAdminRequest = { firstName: '', lastName: '', email: '', role: 'admin' };

const OrgDetailModal: React.FC<OrgDetailModalProps> = ({ isOpen, onClose, org, onOrgUpdated, onOrgDeleted }) => {
    const [tab, setTab] = useState<DetailTab>('details');

    // Details tab state
    const [detailsForm, setDetailsForm] = useState<Partial<Organisation>>({});
    const [detailsErrors, setDetailsErrors] = useState<Partial<Record<string, string>>>({});
    const [isSaving, setIsSaving] = useState(false);

    // Users tab state
    const [users, setUsers] = useState<OrgUser[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [inviteForm, setInviteForm] = useState<InviteAdminRequest>(emptyInvite);
    const [inviteErrors, setInviteErrors] = useState<Partial<Record<keyof InviteAdminRequest, string>>>({});
    const [isInviting, setIsInviting] = useState(false);

    // Danger zone state
    const [deleteConfirmName, setDeleteConfirmName] = useState('');
    const [isDeactivating, setIsDeactivating] = useState(false);

    // Org reactivation panel state
    const [showReactivatePanel, setShowReactivatePanel] = useState(false);
    const [reactivatableUsers, setReactivatableUsers] = useState<OrgUser[]>([]);
    const [isLoadingReactivatable, setIsLoadingReactivatable] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [isConfirmingReactivation, setIsConfirmingReactivation] = useState(false);

    // Per-user action confirm state
    const [confirmingDeactivate, setConfirmingDeactivate] = useState<string | null>(null);
    const [confirmingReactivate, setConfirmingReactivate] = useState<string | null>(null);
    const [confirmingCancelInvite, setConfirmingCancelInvite] = useState<string | null>(null);
    const [resendingInvite, setResendingInvite] = useState<string | null>(null);
    const [resetPasswordUser, setResetPasswordUser] = useState<OrgUser | null>(null);

    // Sync details form when org changes
    useEffect(() => {
        if (org) {
            setDetailsForm({
                name: org.name,
                contactEmail: org.contactEmail,
                contactPhone: org.contactPhone ?? '',
                type: org.type,
                subtype: org.subtype,
                consentMethod: org.consentMethod,
                automatedChecks: org.automatedChecks,
                trustidEnabled: org.trustidEnabled,
                demoMode: org.demoMode,
            });
            setDetailsErrors({});
        }
    }, [org]);

    // Load users when switching to users tab
    useEffect(() => {
        if (isOpen && org && tab === 'users') {
            loadUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, org, tab]);

    const loadUsers = async () => {
        if (!org) return;
        setIsLoadingUsers(true);
        try {
            const result = await apiClient.getOrgUsers(org.id);
            setUsers(result.users);
        } catch (err: any) {
            toast.error(err.message || 'Failed to load users');
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleClose = () => {
        setTab('details');
        setShowInviteForm(false);
        setInviteForm(emptyInvite);
        setInviteErrors({});
        setDeleteConfirmName('');
        setConfirmingDeactivate(null);
        setConfirmingReactivate(null);
        setConfirmingCancelInvite(null);
        setResendingInvite(null);
        setResetPasswordUser(null);
        setShowReactivatePanel(false);
        setReactivatableUsers([]);
        setSelectedUserIds([]);
        onClose();
    };

    // ── Details save ──────────────────────────────────────────────────────────

    const isDetailsDirty = org
        ? detailsForm.name !== org.name ||
          detailsForm.contactEmail !== org.contactEmail ||
          (detailsForm.contactPhone ?? '') !== (org.contactPhone ?? '') ||
          detailsForm.subtype !== org.subtype ||
          detailsForm.consentMethod !== org.consentMethod ||
          !!detailsForm.automatedChecks !== !!org.automatedChecks ||
          !!detailsForm.trustidEnabled !== !!org.trustidEnabled ||
          !!detailsForm.demoMode !== !!org.demoMode
        : false;

    const validateDetails = (): boolean => {
        const errors: Partial<Record<string, string>> = {};
        if (!detailsForm.name?.trim()) errors.name = 'Organisation name is required';
        if (!detailsForm.contactEmail?.trim()) errors.contactEmail = 'Contact email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(detailsForm.contactEmail)) errors.contactEmail = 'Invalid email address';
        setDetailsErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSaveDetails = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!org || !validateDetails()) return;

        setIsSaving(true);
        try {
            const payload: Parameters<typeof apiClient.updateOrganisationById>[1] = {};
            if (detailsForm.name !== org.name) payload.name = detailsForm.name;
            if (detailsForm.contactEmail !== org.contactEmail) payload.contactEmail = detailsForm.contactEmail;
            const phone = detailsForm.contactPhone?.trim() || undefined;
            if ((phone ?? null) !== (org.contactPhone ?? null)) payload.contactPhone = phone;
            if (detailsForm.subtype !== org.subtype) payload.subtype = detailsForm.subtype;
            if (detailsForm.consentMethod !== org.consentMethod) payload.consentMethod = detailsForm.consentMethod;
            if (!!detailsForm.automatedChecks !== !!org.automatedChecks) payload.automatedChecks = !!detailsForm.automatedChecks;
            if (!!detailsForm.trustidEnabled !== !!org.trustidEnabled) payload.trustidEnabled = !!detailsForm.trustidEnabled;
            if (!!detailsForm.demoMode !== !!org.demoMode) payload.demoMode = !!detailsForm.demoMode;

            const result = await apiClient.updateOrganisationById(org.id, payload);
            onOrgUpdated(result.organisation);
            toast.success('Organisation updated');
        } catch (err: any) {
            toast.error(err.message || 'Failed to update organisation');
        } finally {
            setIsSaving(false);
        }
    };

    // ── Invite user ───────────────────────────────────────────────────────────

    const validateInvite = (): boolean => {
        const errors: Partial<Record<keyof InviteAdminRequest, string>> = {};
        if (!inviteForm.firstName.trim()) errors.firstName = 'First name is required';
        if (!inviteForm.lastName.trim()) errors.lastName = 'Last name is required';
        if (!inviteForm.email.trim()) errors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteForm.email)) errors.email = 'Invalid email address';
        setInviteErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInviteUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!org || !validateInvite()) return;

        setIsInviting(true);
        try {
            await apiClient.inviteAdmin(org.id, inviteForm);
            toast.success(`Invitation sent to ${inviteForm.email}`);
            setInviteForm(emptyInvite);
            setShowInviteForm(false);
            await loadUsers();
        } catch (err: any) {
            toast.error(err.message || 'Failed to send invitation');
        } finally {
            setIsInviting(false);
        }
    };

    // ── Deactivate user ───────────────────────────────────────────────────────

    const handleDeactivateUser = async (userId: string) => {
        if (!org) return;
        try {
            await apiClient.deactivateOrgUser(org.id, userId);
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, isActive: false } : u));
            toast.success('User deactivated');
        } catch (err: any) {
            toast.error(err.message || 'Failed to deactivate user');
        } finally {
            setConfirmingDeactivate(null);
        }
    };

    const handleResendInvite = async (userId: string) => {
        if (!org) return;
        setResendingInvite(userId);
        try {
            await apiClient.resendOrgUserInvite(org.id, userId);
            setUsers(prev => prev.map(u => {
                if (u.id !== userId) return u;
                const newExpiry = new Date();
                newExpiry.setHours(newExpiry.getHours() + 48);
                return { ...u, invitationExpiry: newExpiry.toISOString() };
            }));
            toast.success('Invitation resent');
        } catch (err: any) {
            toast.error(err.message || 'Failed to resend invitation');
        } finally {
            setResendingInvite(null);
        }
    };

    const handleCancelInvite = async (userId: string) => {
        if (!org) return;
        try {
            await apiClient.cancelOrgUserInvite(org.id, userId);
            setUsers(prev => prev.filter(u => u.id !== userId));
            toast.success('Invitation cancelled');
        } catch (err: any) {
            toast.error(err.message || 'Failed to cancel invitation');
        } finally {
            setConfirmingCancelInvite(null);
        }
    };

    const handleReactivateUser = async (userId: string) => {
        if (!org) return;
        try {
            await apiClient.reactivateOrgUser(org.id, userId);
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, isActive: true } : u));
            toast.success('User reactivated');
        } catch (err: any) {
            toast.error(err.message || 'Failed to reactivate user');
        } finally {
            setConfirmingReactivate(null);
        }
    };

    // ── Danger zone ───────────────────────────────────────────────────────────

    const handleDeactivate = async () => {
        if (!org) return;
        setIsDeactivating(true);
        try {
            await apiClient.deleteOrganisation(org.id);
            toast.success(`${org.name} has been deactivated`);
            onOrgDeleted(org.id);
            handleClose();
        } catch (err: any) {
            toast.error(err.message || 'Failed to deactivate organisation');
        } finally {
            setIsDeactivating(false);
        }
    };

    const handleOpenReactivatePanel = async () => {
        if (!org) return;
        setShowReactivatePanel(true);
        setIsLoadingReactivatable(true);
        try {
            const result = await apiClient.getOrgUsers(org.id);
            const inactive = result.users.filter(u => !u.isActive);
            setReactivatableUsers(inactive);
            setSelectedUserIds(inactive.map(u => u.id));
        } catch (err: any) {
            toast.error(err.message || 'Failed to load users');
        } finally {
            setIsLoadingReactivatable(false);
        }
    };

    const handleConfirmReactivation = async () => {
        if (!org) return;
        setIsConfirmingReactivation(true);
        try {
            const result = await apiClient.updateOrganisationById(org.id, { isActive: true });
            await Promise.all(selectedUserIds.map(userId => apiClient.reactivateOrgUser(org.id, userId)));
            onOrgUpdated(result.organisation);
            const userCount = selectedUserIds.length;
            toast.success(`${org.name} reactivated${userCount > 0 ? ` with ${userCount} user${userCount !== 1 ? 's' : ''}` : ''}`);
            handleClose();
        } catch (err: any) {
            toast.error(err.message || 'Failed to reactivate organisation');
        } finally {
            setIsConfirmingReactivation(false);
        }
    };

    if (!isOpen || !org) return null;

    const canConfirmDelete = deleteConfirmName === org.name;

    return (
        <>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-start p-6 border-b border-gray-200 flex-shrink-0">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">{org.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                            <OrgTypeBadge type={org.type} />
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${org.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {org.isActive ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                    </div>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors mt-0.5">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 flex-shrink-0">
                    {([
                        { key: 'details', label: 'Details', Icon: Pencil },
                        { key: 'users', label: 'Users', Icon: Users },
                        { key: 'danger', label: 'Danger Zone', Icon: AlertTriangle },
                    ] as { key: DetailTab; label: string; Icon: React.ElementType }[]).map(({ key, label, Icon }) => (
                        <button
                            key={key}
                            onClick={() => setTab(key)}
                            className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                                tab === key
                                    ? key === 'danger'
                                        ? 'border-red-500 text-red-600'
                                        : 'border-green-500 text-green-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Icon className="w-4 h-4" />
                            {label}
                        </button>
                    ))}
                </div>

                {/* Tab content */}
                <div className="flex-1 overflow-y-auto">
                    {/* ── Details Tab ── */}
                    {tab === 'details' && (
                        <form onSubmit={handleSaveDetails} className="p-6 space-y-4">
                            {!org.isActive && (
                                <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                    This organisation is inactive. Reactivate it before making changes.
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Organisation Name *</label>
                                <input
                                    type="text"
                                    disabled={!org.isActive}
                                    className={inputClass(!!detailsErrors.name)}
                                    value={detailsForm.name ?? ''}
                                    onChange={e => { setDetailsForm(p => ({ ...p, name: e.target.value })); setDetailsErrors(p => ({ ...p, name: undefined })); }}
                                />
                                {detailsErrors.name && <p className="mt-1 text-xs text-red-600">{detailsErrors.name}</p>}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                    <input
                                        type="text"
                                        disabled
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 capitalize"
                                        value={detailsForm.type ?? ''}
                                    />
                                </div>
                                {detailsForm.type === 'fleet' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Subtype</label>
                                        <select
                                            disabled={!org.isActive}
                                            className={inputClass(false)}
                                            value={detailsForm.subtype ?? 'general'}
                                            onChange={e => setDetailsForm(p => ({ ...p, subtype: e.target.value as FleetSubtype }))}
                                        >
                                            {FLEET_SUBTYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                        </select>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email *</label>
                                <input
                                    type="email"
                                    disabled={!org.isActive}
                                    className={inputClass(!!detailsErrors.contactEmail)}
                                    value={detailsForm.contactEmail ?? ''}
                                    onChange={e => { setDetailsForm(p => ({ ...p, contactEmail: e.target.value })); setDetailsErrors(p => ({ ...p, contactEmail: undefined })); }}
                                />
                                {detailsErrors.contactEmail && <p className="mt-1 text-xs text-red-600">{detailsErrors.contactEmail}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                                <input
                                    type="tel"
                                    placeholder="Optional"
                                    disabled={!org.isActive}
                                    className={inputClass(false)}
                                    value={detailsForm.contactPhone ?? ''}
                                    onChange={e => setDetailsForm(p => ({ ...p, contactPhone: e.target.value }))}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Consent Method</label>
                                <div className="space-y-2">
                                    {CONSENT_METHODS.map(m => (
                                        <label
                                            key={m.value}
                                            className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-colors ${!org.isActive ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${detailsForm.consentMethod === m.value ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
                                        >
                                            <input
                                                type="radio"
                                                name="consentMethod"
                                                value={m.value}
                                                disabled={!org.isActive}
                                                checked={detailsForm.consentMethod === m.value}
                                                onChange={() => setDetailsForm(p => ({ ...p, consentMethod: m.value }))}
                                                className="mt-0.5 accent-green-500"
                                            />
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">{m.label}</p>
                                                <p className="text-xs text-gray-500">{m.description}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Paid Add-ons</label>
                                <div className="space-y-2">
                                    <label className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-colors ${!org.isActive ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${detailsForm.automatedChecks ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                        <input
                                            type="checkbox"
                                            disabled={!org.isActive}
                                            checked={!!detailsForm.automatedChecks}
                                            onChange={e => setDetailsForm(p => ({ ...p, automatedChecks: e.target.checked }))}
                                            className="mt-0.5 w-4 h-4 accent-green-600"
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">Automated checks</p>
                                            <p className="text-xs text-gray-500">Enables scheduled licence checks and automatic checks when consent is approved.</p>
                                        </div>
                                    </label>
                                    <label className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-colors ${!org.isActive ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${detailsForm.trustidEnabled ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                        <input
                                            type="checkbox"
                                            disabled={!org.isActive}
                                            checked={!!detailsForm.trustidEnabled}
                                            onChange={e => setDetailsForm(p => ({ ...p, trustidEnabled: e.target.checked }))}
                                            className="mt-0.5 w-4 h-4 accent-green-600"
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">DBS checks</p>
                                            <p className="text-xs text-gray-500">Enables DBS checking via TrustID for drivers in this organisation.</p>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Feature Restrictions</label>
                                <label className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-colors ${!org.isActive ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${detailsForm.demoMode ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                    <input
                                        type="checkbox"
                                        disabled={!org.isActive}
                                        checked={!!detailsForm.demoMode}
                                        onChange={e => setDetailsForm(p => ({ ...p, demoMode: e.target.checked }))}
                                        className="mt-0.5 w-4 h-4 accent-amber-500"
                                    />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">Demo mode</p>
                                        <p className="text-xs text-gray-500">Automation settings are view-only and CSV driver import is disabled for this organisation.</p>
                                    </div>
                                </label>
                            </div>

                            <div className="flex justify-end pt-4 border-t border-gray-200">
                                <button
                                    type="submit"
                                    disabled={isSaving || !isDetailsDirty || !org.isActive}
                                    className="flex items-center px-4 py-2 text-sm text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* ── Users Tab ── */}
                    {tab === 'users' && (
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <p className="text-sm text-gray-500">{users.length} user{users.length !== 1 ? 's' : ''}</p>
                                {!showInviteForm && (
                                    <button
                                        onClick={() => setShowInviteForm(true)}
                                        disabled={!org.isActive}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ backgroundColor: '#69BE28' }}
                                    >
                                        <UserPlus className="w-4 h-4" />
                                        Invite User
                                    </button>
                                )}
                            </div>

                            {/* Invite form */}
                            {showInviteForm && (
                                <form onSubmit={handleInviteUser} className="mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                                    <p className="text-sm font-medium text-gray-700">Invite a new user</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="First name *"
                                                className={inputClass(!!inviteErrors.firstName)}
                                                value={inviteForm.firstName}
                                                onChange={e => { setInviteForm(p => ({ ...p, firstName: e.target.value })); setInviteErrors(p => ({ ...p, firstName: undefined })); }}
                                            />
                                            {inviteErrors.firstName && <p className="mt-1 text-xs text-red-600">{inviteErrors.firstName}</p>}
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="Last name *"
                                                className={inputClass(!!inviteErrors.lastName)}
                                                value={inviteForm.lastName}
                                                onChange={e => { setInviteForm(p => ({ ...p, lastName: e.target.value })); setInviteErrors(p => ({ ...p, lastName: undefined })); }}
                                            />
                                            {inviteErrors.lastName && <p className="mt-1 text-xs text-red-600">{inviteErrors.lastName}</p>}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <input
                                                type="email"
                                                placeholder="Email address *"
                                                className={inputClass(!!inviteErrors.email)}
                                                value={inviteForm.email}
                                                onChange={e => { setInviteForm(p => ({ ...p, email: e.target.value })); setInviteErrors(p => ({ ...p, email: undefined })); }}
                                            />
                                            {inviteErrors.email && <p className="mt-1 text-xs text-red-600">{inviteErrors.email}</p>}
                                        </div>
                                        <div>
                                            <select
                                                className={inputClass(false)}
                                                value={inviteForm.role ?? 'admin'}
                                                onChange={e => setInviteForm(p => ({ ...p, role: e.target.value as InviteAdminRequest['role'] }))}
                                            >
                                                {ORG_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-2 pt-1">
                                        <button
                                            type="button"
                                            onClick={() => { setShowInviteForm(false); setInviteForm(emptyInvite); setInviteErrors({}); }}
                                            className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isInviting}
                                            className="flex items-center px-3 py-1.5 text-sm text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                            style={{ backgroundColor: '#69BE28' }}
                                        >
                                            {isInviting && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                                            {isInviting ? 'Sending...' : 'Send Invitation'}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* Users list */}
                            {isLoadingUsers ? (
                                <div className="flex items-center justify-center py-10">
                                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                </div>
                            ) : users.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                                    <Users className="w-8 h-8 mb-2" />
                                    <p className="text-sm">No users yet</p>
                                </div>
                            ) : (
                                <div className="rounded-lg border border-gray-200 overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Added</th>
                                                <th className="px-4 py-2.5" />
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {users.map(u => {
                                                const status = getUserStatus(u);
                                                return (
                                                    <tr key={u.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 font-medium text-gray-900">{u.firstName} {u.lastName}</td>
                                                        <td className="px-4 py-3 text-gray-500">{u.email}</td>
                                                        <td className="px-4 py-3">
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${ROLE_BADGE[u.role] ?? 'bg-gray-100 text-gray-600'}`}>
                                                                {u.role}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[status]}`}>
                                                                {STATUS_LABEL[status]}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-400 text-xs">
                                                            {new Date(u.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            {status === 'active' && (
                                                                confirmingDeactivate === u.id ? (
                                                                    <span className="inline-flex items-center gap-1.5">
                                                                        <button onClick={() => handleDeactivateUser(u.id)} className="px-2 py-1 text-xs text-white bg-red-600 rounded hover:bg-red-700 transition-colors">Confirm</button>
                                                                        <button onClick={() => setConfirmingDeactivate(null)} className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors">Cancel</button>
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center gap-1">
                                                                        <button onClick={() => setResetPasswordUser(u)} title="Reset password" disabled={!org.isActive} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                                                                            <Lock className="w-4 h-4" />
                                                                        </button>
                                                                        <button onClick={() => setConfirmingDeactivate(u.id)} title="Deactivate user" disabled={!org.isActive} className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                                                                            <UserX className="w-4 h-4" />
                                                                        </button>
                                                                    </span>
                                                                )
                                                            )}
                                                            {(status === 'pending' || status === 'expired') && (
                                                                confirmingCancelInvite === u.id ? (
                                                                    <span className="inline-flex items-center gap-1.5">
                                                                        <button onClick={() => handleCancelInvite(u.id)} className="px-2 py-1 text-xs text-white bg-red-600 rounded hover:bg-red-700 transition-colors">Confirm</button>
                                                                        <button onClick={() => setConfirmingCancelInvite(null)} className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors">Cancel</button>
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center gap-1">
                                                                        {status === 'expired' && (
                                                                            <button
                                                                                onClick={() => handleResendInvite(u.id)}
                                                                                disabled={resendingInvite === u.id || !org.isActive}
                                                                                title="Resend invitation"
                                                                                className="p-1.5 hover:bg-green-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                                                style={{ color: '#69BE28' }}
                                                                            >
                                                                                {resendingInvite === u.id
                                                                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                                                                    : <MailPlus className="w-4 h-4" />}
                                                                            </button>
                                                                        )}
                                                                        <button onClick={() => setConfirmingCancelInvite(u.id)} disabled={!org.isActive} title={status === 'expired' ? 'Remove user' : 'Cancel invitation'} className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                                                                            <MailX className="w-4 h-4" />
                                                                        </button>
                                                                    </span>
                                                                )
                                                            )}
                                                            {status === 'inactive' && (
                                                                confirmingReactivate === u.id ? (
                                                                    <span className="inline-flex items-center gap-1.5">
                                                                        <button onClick={() => handleReactivateUser(u.id)} className="px-2 py-1 text-xs text-white rounded hover:opacity-90 transition-opacity" style={{ backgroundColor: '#69BE28' }}>Confirm</button>
                                                                        <button onClick={() => setConfirmingReactivate(null)} className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors">Cancel</button>
                                                                    </span>
                                                                ) : (
                                                                    <button onClick={() => setConfirmingReactivate(u.id)} title="Reactivate user" disabled={!org.isActive} className="p-1.5 hover:bg-green-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed" style={{ color: '#69BE28' }}>
                                                                        <UserCheck className="w-4 h-4" />
                                                                    </button>
                                                                )
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Danger Zone Tab ── */}
                    {tab === 'danger' && (
                        <div className="p-6 space-y-6">
                            {org.isActive ? (
                                <div className="rounded-lg border border-red-200 bg-red-50 p-5">
                                    <div className="flex items-start gap-3 mb-4">
                                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <h4 className="text-sm font-semibold text-red-800">Deactivate Organisation</h4>
                                            <p className="text-sm text-red-700 mt-1">
                                                This will deactivate <span className="font-medium">{org.name}</span> and block all of its users from logging in.
                                                No data will be deleted. The organisation can be reactivated at any time.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-red-800 mb-1">
                                                Type <span className="font-bold">{org.name}</span> to confirm
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 bg-white"
                                                placeholder={org.name}
                                                value={deleteConfirmName}
                                                onChange={e => setDeleteConfirmName(e.target.value)}
                                            />
                                        </div>
                                        <button
                                            onClick={handleDeactivate}
                                            disabled={!canConfirmDelete || isDeactivating}
                                            className="flex items-center px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {isDeactivating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                            {isDeactivating ? 'Deactivating...' : 'Deactivate Organisation'}
                                        </button>
                                    </div>
                                </div>
                            ) : showReactivatePanel ? (
                                <div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
                                    <div className="flex items-center justify-between mb-1">
                                        <h4 className="text-sm font-semibold text-gray-700">Select users to reactivate</h4>
                                        <button onClick={() => setShowReactivatePanel(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mb-4">
                                        <span className="font-medium">{org.name}</span> will be reactivated. Choose which users should regain access.
                                    </p>

                                    {isLoadingReactivatable ? (
                                        <div className="flex items-center justify-center py-6">
                                            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                        </div>
                                    ) : reactivatableUsers.length === 0 ? (
                                        <p className="text-sm text-gray-500 mb-4">No inactive users. Only the organisation will be reactivated.</p>
                                    ) : (
                                        <div className="space-y-1 mb-4 max-h-48 overflow-y-auto">
                                            <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 accent-[#69BE28]"
                                                    checked={selectedUserIds.length === reactivatableUsers.length}
                                                    onChange={e => setSelectedUserIds(e.target.checked ? reactivatableUsers.map(u => u.id) : [])}
                                                />
                                                <span className="text-xs font-medium text-gray-600">Select all ({reactivatableUsers.length})</span>
                                            </label>
                                            <div className="border-t border-gray-200 pt-1">
                                                {reactivatableUsers.map(u => (
                                                    <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            className="w-4 h-4 accent-[#69BE28]"
                                                            checked={selectedUserIds.includes(u.id)}
                                                            onChange={e => setSelectedUserIds(prev =>
                                                                e.target.checked ? [...prev, u.id] : prev.filter(id => id !== u.id)
                                                            )}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <span className="text-sm text-gray-800">{u.firstName} {u.lastName}</span>
                                                            <span className="text-xs text-gray-400 ml-2 truncate">{u.email}</span>
                                                        </div>
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${ROLE_BADGE[u.role] ?? 'bg-gray-100 text-gray-600'}`}>
                                                            {u.role}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                                        <button
                                            onClick={() => setShowReactivatePanel(false)}
                                            className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleConfirmReactivation}
                                            disabled={isConfirmingReactivation}
                                            className="flex items-center px-4 py-1.5 text-sm text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                            style={{ backgroundColor: '#69BE28' }}
                                        >
                                            {isConfirmingReactivation && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                            {isConfirmingReactivation
                                                ? 'Reactivating...'
                                                : `Reactivate${selectedUserIds.length > 0 ? ` (${selectedUserIds.length} user${selectedUserIds.length !== 1 ? 's' : ''})` : ''}`}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
                                    <div className="flex items-start gap-3 mb-4">
                                        <CheckCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-700">Reactivate Organisation</h4>
                                            <p className="text-sm text-gray-600 mt-1">
                                                <span className="font-medium">{org.name}</span> is currently inactive. Click below to choose which users should regain access alongside the organisation.
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleOpenReactivatePanel}
                                        className="flex items-center px-4 py-2 text-sm text-white rounded-lg hover:opacity-90 transition-opacity"
                                        style={{ backgroundColor: '#69BE28' }}
                                    >
                                        Reactivate Organisation
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
        {resetPasswordUser && (
            <ResetPasswordModal
                isOpen={true}
                user={{ id: resetPasswordUser.id, firstName: resetPasswordUser.firstName, lastName: resetPasswordUser.lastName, email: resetPasswordUser.email }}
                onClose={() => setResetPasswordUser(null)}
                onSuccess={() => setResetPasswordUser(null)}
                orgId={org.id}
            />
        )}
        </>
    );
};

// ─── Create Organisation Modal ────────────────────────────────────────────────

type Step = 'org' | 'admin' | 'done';

interface CreateOrgModalProps {
    isOpen: boolean;
    onClose: () => void;
    onOrgCreated: (org: Organisation) => void;
}

const emptyOrg: CreateOrganisationRequest = {
    name: '',
    type: 'fleet',
    subtype: 'general',
    contactEmail: '',
    contactPhone: '',
    consentMethod: 'email',
};

const emptyAdmin: InviteAdminRequest = { firstName: '', lastName: '', email: '' };

const CreateOrgModal: React.FC<CreateOrgModalProps> = ({ isOpen, onClose, onOrgCreated }) => {
    const [step, setStep] = useState<Step>('org');
    const [orgForm, setOrgForm] = useState<CreateOrganisationRequest>(emptyOrg);
    const [adminForm, setAdminForm] = useState<InviteAdminRequest>(emptyAdmin);
    const [createdOrg, setCreatedOrg] = useState<Organisation | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orgErrors, setOrgErrors] = useState<Partial<Record<keyof CreateOrganisationRequest, string>>>({});
    const [adminErrors, setAdminErrors] = useState<Partial<Record<keyof InviteAdminRequest, string>>>({});

    const reset = () => {
        setStep('org');
        setOrgForm(emptyOrg);
        setAdminForm(emptyAdmin);
        setCreatedOrg(null);
        setOrgErrors({});
        setAdminErrors({});
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const validateOrg = (): boolean => {
        const errors: Partial<Record<keyof CreateOrganisationRequest, string>> = {};
        if (!orgForm.name.trim()) errors.name = 'Organisation name is required';
        if (!orgForm.contactEmail.trim()) errors.contactEmail = 'Contact email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orgForm.contactEmail)) errors.contactEmail = 'Invalid email address';
        setOrgErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateAdmin = (): boolean => {
        const errors: Partial<Record<keyof InviteAdminRequest, string>> = {};
        if (!adminForm.firstName.trim()) errors.firstName = 'First name is required';
        if (!adminForm.lastName.trim()) errors.lastName = 'Last name is required';
        if (!adminForm.email.trim()) errors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminForm.email)) errors.email = 'Invalid email address';
        setAdminErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateOrg = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateOrg()) return;

        setIsSubmitting(true);
        try {
            const payload: CreateOrganisationRequest = {
                ...orgForm,
                subtype: orgForm.type === 'fleet' ? orgForm.subtype : undefined,
                contactPhone: orgForm.contactPhone?.trim() || undefined,
            };
            const result = await apiClient.createOrganisation(payload);
            setCreatedOrg(result.organisation);
            onOrgCreated(result.organisation);
            toast.success('Organisation created successfully');
            setStep('admin');
        } catch (err: any) {
            toast.error(err.message || 'Failed to create organisation');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInviteAdmin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateAdmin() || !createdOrg) return;

        setIsSubmitting(true);
        try {
            await apiClient.inviteAdmin(createdOrg.id, adminForm);
            toast.success(`Invitation sent to ${adminForm.email}`);
            setStep('done');
        } catch (err: any) {
            toast.error(err.message || 'Failed to send invitation');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSkipAdmin = () => setStep('done');

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            {step === 'org' && 'New Organisation'}
                            {step === 'admin' && 'Invite Admin'}
                            {step === 'done' && 'Setup Complete'}
                        </h3>
                        {step !== 'done' && (
                            <div className="flex items-center mt-1 space-x-1 text-xs text-gray-500">
                                <span className={step === 'org' ? 'text-green-600 font-medium' : 'text-gray-400'}>1. Organisation</span>
                                <ChevronRight className="w-3 h-3" />
                                <span className={step === 'admin' ? 'text-green-600 font-medium' : 'text-gray-400'}>2. Invite Admin</span>
                            </div>
                        )}
                    </div>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Step 1: Organisation Details */}
                {step === 'org' && (
                    <form onSubmit={handleCreateOrg} className="p-6 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Organisation Name *</label>
                            <input
                                type="text"
                                placeholder="e.g., Acme Transport Ltd"
                                className={inputClass(!!orgErrors.name)}
                                value={orgForm.name}
                                onChange={e => { setOrgForm(p => ({ ...p, name: e.target.value })); setOrgErrors(p => ({ ...p, name: undefined })); }}
                            />
                            {orgErrors.name && <p className="mt-1 text-xs text-red-600">{orgErrors.name}</p>}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                                <select
                                    className={inputClass(false)}
                                    value={orgForm.type}
                                    onChange={e => setOrgForm(p => ({ ...p, type: e.target.value as OrgType, subtype: 'general' }))}
                                >
                                    {ORG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>

                            {orgForm.type === 'fleet' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtype</label>
                                    <select
                                        className={inputClass(false)}
                                        value={orgForm.subtype}
                                        onChange={e => setOrgForm(p => ({ ...p, subtype: e.target.value as FleetSubtype }))}
                                    >
                                        {FLEET_SUBTYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email *</label>
                            <input
                                type="email"
                                placeholder="admin@company.com"
                                className={inputClass(!!orgErrors.contactEmail)}
                                value={orgForm.contactEmail}
                                onChange={e => { setOrgForm(p => ({ ...p, contactEmail: e.target.value })); setOrgErrors(p => ({ ...p, contactEmail: undefined })); }}
                            />
                            {orgErrors.contactEmail && <p className="mt-1 text-xs text-red-600">{orgErrors.contactEmail}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                            <input
                                type="tel"
                                placeholder="+44 7700 000000 (optional)"
                                className={inputClass(false)}
                                value={orgForm.contactPhone}
                                onChange={e => setOrgForm(p => ({ ...p, contactPhone: e.target.value }))}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Consent Method *</label>
                            <div className="space-y-2">
                                {CONSENT_METHODS.map(m => (
                                    <label
                                        key={m.value}
                                        className={`flex items-start space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${orgForm.consentMethod === m.value ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
                                    >
                                        <input
                                            type="radio"
                                            name="consentMethod"
                                            value={m.value}
                                            checked={orgForm.consentMethod === m.value}
                                            onChange={() => setOrgForm(p => ({ ...p, consentMethod: m.value }))}
                                            className="mt-0.5 accent-green-500"
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{m.label}</p>
                                            <p className="text-xs text-gray-500">{m.description}</p>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                            <button type="button" onClick={handleClose} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-4 py-2 text-sm text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                {isSubmitting ? 'Creating...' : 'Create Organisation'}
                            </button>
                        </div>
                    </form>
                )}

                {/* Step 2: Invite Admin */}
                {step === 'admin' && (
                    <form onSubmit={handleInviteAdmin} className="p-6 space-y-4">
                        <p className="text-sm text-gray-600">
                            <span className="font-medium">{createdOrg?.name}</span> has been created. Invite their first admin user. They'll receive an email to set their password.
                        </p>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                                <input
                                    type="text"
                                    placeholder="Jane"
                                    className={inputClass(!!adminErrors.firstName)}
                                    value={adminForm.firstName}
                                    onChange={e => { setAdminForm(p => ({ ...p, firstName: e.target.value })); setAdminErrors(p => ({ ...p, firstName: undefined })); }}
                                />
                                {adminErrors.firstName && <p className="mt-1 text-xs text-red-600">{adminErrors.firstName}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                                <input
                                    type="text"
                                    placeholder="Smith"
                                    className={inputClass(!!adminErrors.lastName)}
                                    value={adminForm.lastName}
                                    onChange={e => { setAdminForm(p => ({ ...p, lastName: e.target.value })); setAdminErrors(p => ({ ...p, lastName: undefined })); }}
                                />
                                {adminErrors.lastName && <p className="mt-1 text-xs text-red-600">{adminErrors.lastName}</p>}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                            <input
                                type="email"
                                placeholder="jane.smith@company.com"
                                className={inputClass(!!adminErrors.email)}
                                value={adminForm.email}
                                onChange={e => { setAdminForm(p => ({ ...p, email: e.target.value })); setAdminErrors(p => ({ ...p, email: undefined })); }}
                            />
                            {adminErrors.email && <p className="mt-1 text-xs text-red-600">{adminErrors.email}</p>}
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                            <button type="button" onClick={handleSkipAdmin} className="text-sm text-gray-500 hover:text-gray-700 underline">
                                Skip for now
                            </button>
                            <div className="flex space-x-3">
                                <button type="button" onClick={handleClose} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 text-sm text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    {isSubmitting ? 'Sending...' : 'Send Invitation'}
                                </button>
                            </div>
                        </div>
                    </form>
                )}

                {/* Step 3: Done */}
                {step === 'done' && (
                    <div className="p-6 text-center space-y-4">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
                        <div>
                            <h4 className="text-base font-semibold text-gray-900">{createdOrg?.name} is ready</h4>
                            <p className="text-sm text-gray-500 mt-1">
                                {adminForm.email
                                    ? `An invitation has been sent to ${adminForm.email}.`
                                    : 'You can invite users from the organisation details.'}
                            </p>
                        </div>
                        <button
                            onClick={handleClose}
                            className="px-6 py-2 text-sm text-white rounded-lg hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: '#69BE28' }}
                        >
                            Done
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Activity Feed ────────────────────────────────────────────────────────────

const ACTION_META: Record<string, { label: string; colour: string }> = {
    ORGANISATION_CREATED:     { label: 'Organisation created',      colour: 'bg-green-100 text-green-700' },
    ORGANISATION_UPDATED:     { label: 'Organisation updated',      colour: 'bg-blue-100 text-blue-700' },
    ORGANISATION_DELETED:     { label: 'Organisation deactivated',  colour: 'bg-red-100 text-red-700' },
    ORGANISATION_REACTIVATED: { label: 'Organisation reactivated',  colour: 'bg-green-100 text-green-700' },
    USER_CREATED:             { label: 'User invited',              colour: 'bg-green-100 text-green-700' },
    USER_INVITED:             { label: 'Invitation resent',         colour: 'bg-amber-100 text-amber-700' },
    USER_DEACTIVATED:         { label: 'User deactivated',          colour: 'bg-red-100 text-red-700' },
    USER_ACTIVATED:           { label: 'User reactivated',          colour: 'bg-green-100 text-green-700' },
    USER_DELETED:             { label: 'Invitation cancelled',      colour: 'bg-orange-100 text-orange-700' },
};

function getActionMeta(entry: AdminActivityEntry): { label: string; colour: string } {
    return ACTION_META[entry.action] ?? { label: entry.action, colour: 'bg-gray-100 text-gray-600' };
}

function formatUser(obj: any): string {
    if (!obj) return '';
    const name = obj.firstName && obj.lastName ? `${obj.firstName} ${obj.lastName}` : '';
    const email = obj.email ? `(${obj.email})` : '';
    const role = obj.role ? `· ${obj.role}` : '';
    return [name, email, role].filter(Boolean).join(' ');
}

function getActivityDetail(entry: AdminActivityEntry): string {
    const nv = entry.newValue;
    const pv = entry.previousValue;

    switch (entry.action) {
        case 'ORGANISATION_CREATED':
        case 'ORGANISATION_REACTIVATED':
            return entry.organisation?.name ?? '';
        case 'ORGANISATION_UPDATED':
        case 'ORGANISATION_DELETED':
            if (nv?.usersDeactivated !== undefined) {
                return `${nv.usersDeactivated} user${nv.usersDeactivated !== 1 ? 's' : ''} deactivated, ${nv.invitesCancelled} invite${nv.invitesCancelled !== 1 ? 's' : ''} cancelled`;
            }
            if (nv?.isActive === true) return '';
            if (nv?.isActive === false) return '';
            if (nv) {
                const changed = Object.keys(nv).filter(k => k !== 'isActive');
                if (changed.length) return `Updated: ${changed.join(', ')}`;
            }
            return '';
        case 'USER_CREATED':
        case 'USER_INVITED':
        case 'USER_DEACTIVATED':
        case 'USER_ACTIVATED':
            return formatUser(nv);
        case 'USER_DELETED':
            return formatUser(pv);
        default:
            return '';
    }
}

const ActivityFeed: React.FC = () => {
    const [entries, setEntries] = useState<AdminActivityEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const [search, setSearch] = useState('');
    const [selectedAction, setSelectedAction] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const filtersRef = React.useRef({ search, selectedAction, fromDate, toDate });
    filtersRef.current = { search, selectedAction, fromDate, toDate };

    const hasFilters = search || selectedAction || fromDate || toDate;

    const load = React.useCallback(async (p: number, fresh: boolean) => {
        const f = filtersRef.current;
        if (fresh) setIsLoading(true); else setIsLoadingMore(true);
        try {
            const result = await apiClient.getAdminActivity(p, 20, {
                search: f.search || undefined,
                action: f.selectedAction || undefined,
                fromDate: f.fromDate || undefined,
                toDate: f.toDate || undefined,
            });
            setEntries(prev => fresh ? result.entries : [...prev, ...result.entries]);
            setPage(result.page);
            setTotalPages(result.totalPages);
            setTotal(result.total);
        } catch (err: any) {
            toast.error(err.message || 'Failed to load activity');
        } finally {
            if (fresh) setIsLoading(false); else setIsLoadingMore(false);
        }
    }, []);

    useEffect(() => { load(1, true); }, [load]);

    // Re-fetch when filters change (debounce search)
    useEffect(() => {
        const timer = setTimeout(() => load(1, true), search ? 300 : 0);
        return () => clearTimeout(timer);
    }, [search, selectedAction, fromDate, toDate, load]);

    return (
        <div>
            {/* Filters */}
            <div className="p-4 border-b border-gray-200 flex flex-wrap gap-2">
                <div className="relative flex-1 min-w-40">
                    <input
                        type="text"
                        placeholder="Search org or actor…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                    />
                    <svg className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 111 11a6 6 0 0116 0z" /></svg>
                </div>
                <select
                    value={selectedAction}
                    onChange={e => setSelectedAction(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-400 bg-white"
                >
                    <option value="">All actions</option>
                    {Object.entries(ACTION_META).map(([key, { label }]) => (
                        <option key={key} value={key}>{label}</option>
                    ))}
                </select>
                <input
                    type="date"
                    value={fromDate}
                    onChange={e => setFromDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                />
                <input
                    type="date"
                    value={toDate}
                    onChange={e => setToDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                />
                {hasFilters && (
                    <button
                        onClick={() => { setSearch(''); setSelectedAction(''); setFromDate(''); setToDate(''); }}
                        className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Clear
                    </button>
                )}
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
            ) : entries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                    <Activity className="w-10 h-10 mb-3" />
                    <p className="text-sm">{hasFilters ? 'No results for these filters' : 'No activity yet'}</p>
                </div>
            ) : (
            <>
            {hasFilters && (
                <p className="px-6 py-2 text-xs text-gray-400 border-b border-gray-100">{total} result{total !== 1 ? 's' : ''}</p>
            )}
            <div className="space-y-0 divide-y divide-gray-100">
                {entries.map(entry => {
                    const meta = getActionMeta(entry);
                    const detail = getActivityDetail(entry);
                    const date = new Date(entry.timestamp);
                    return (
                        <div key={entry.id} className="flex items-start gap-4 py-4 px-6 hover:bg-gray-50 transition-colors">
                            <div className="flex-shrink-0 pt-0.5">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${meta.colour}`}>
                                    {meta.label}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    {entry.organisation && (
                                        <span className="text-sm font-medium text-gray-900">{entry.organisation.name}</span>
                                    )}
                                    {detail && (
                                        <span className="text-sm text-gray-500 truncate">{detail}</span>
                                    )}
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    {entry.user ? `${entry.user.firstName} ${entry.user.lastName}` : 'Unknown'}
                                    {' · '}
                                    {date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    {' '}
                                    {date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {page < totalPages && (
                <div className="flex justify-center py-5 border-t border-gray-100">
                    <button
                        onClick={() => load(page + 1, false)}
                        disabled={isLoadingMore}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        {isLoadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                        {isLoadingMore ? 'Loading...' : 'Load more'}
                    </button>
                </div>
            )}
            </>
            )}
        </div>
    );
};

// ─── Super Admin Page ─────────────────────────────────────────────────────────

const SuperAdminPage: React.FC<SuperAdminPageProps> = ({ user, onLogout }) => {
    const [pageTab, setPageTab] = useState<'organisations' | 'activity' | 'health'>('organisations');
    const [organisations, setOrganisations] = useState<Organisation[]>([]);
    const [orgSearch, setOrgSearch] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState<Organisation | null>(null);

    const loadOrganisations = useCallback(async () => {
        try {
            const result = await apiClient.getAllOrganisations();
            setOrganisations(result.organisations);
        } catch (err: any) {
            toast.error(err.message || 'Failed to load organisations');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadOrganisations(); }, [loadOrganisations]);

    const handleOrgCreated = (org: Organisation) => {
        setOrganisations(prev => [...prev, org].sort((a, b) => a.name.localeCompare(b.name)));
    };

    const handleOrgUpdated = (updated: Organisation) => {
        setOrganisations(prev => prev.map(o => o.id === updated.id ? updated : o).sort((a, b) => a.name.localeCompare(b.name)));
        setSelectedOrg(updated);
    };

    const handleOrgDeleted = (orgId: string) => {
        setOrganisations(prev => prev.map(o => o.id === orgId ? { ...o, isActive: false } : o));
        setSelectedOrg(null);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top bar */}
            <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <Building2 className="w-6 h-6 text-gray-700" />
                    <div>
                        <h1 className="text-base font-semibold text-gray-900">IntelliDrive Admin</h1>
                        <p className="text-xs text-gray-500">Organisation Management</p>
                    </div>
                </div>
                <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600">{user.firstName} {user.lastName}</span>
                    <button
                        onClick={onLogout}
                        className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <LogOut className="w-4 h-4" />
                        <span>Sign out</span>
                    </button>
                </div>
            </header>

            {/* Main content */}
            <main className="max-w-6xl mx-auto px-6 py-8">
                {/* Page-level tabs */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                        {([
                            { key: 'organisations', label: 'Organisations', Icon: Building2 },
                            { key: 'activity', label: 'Activity', Icon: Activity },
                            { key: 'health', label: 'System Health', Icon: CheckCircle },
                        ] as { key: 'organisations' | 'activity' | 'health'; label: string; Icon: React.ElementType }[]).map(({ key, label, Icon }) => (
                            <button
                                key={key}
                                onClick={() => setPageTab(key)}
                                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${pageTab === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                <Icon className="w-4 h-4" />
                                {label}
                            </button>
                        ))}
                    </div>
                    {pageTab === 'organisations' && (
                        <button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="flex items-center space-x-2 px-4 py-2 text-sm text-white rounded-lg hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: '#69BE28' }}
                        >
                            <Plus className="w-4 h-4" />
                            <span>New Organisation</span>
                        </button>
                    )}
                </div>

                {pageTab === 'activity' && (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <ActivityFeed />
                    </div>
                )}

                {pageTab === 'health' && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <SystemHealth userRole="super_admin" />
                    </div>
                )}

                {/* Table */}
                {pageTab === 'organisations' && <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    {!isLoading && organisations.length > 0 && (
                        <div className="p-3 border-b border-gray-200">
                            <div className="relative max-w-sm">
                                <input
                                    type="text"
                                    placeholder="Search organisations…"
                                    value={orgSearch}
                                    onChange={e => setOrgSearch(e.target.value)}
                                    className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                                />
                                <svg className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 111 11a6 6 0 0116 0z" /></svg>
                            </div>
                        </div>
                    )}
                    {isLoading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                    ) : organisations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                            <Building2 className="w-10 h-10 mb-3" />
                            <p className="text-sm">No organisations yet</p>
                            <button
                                onClick={() => setIsCreateModalOpen(true)}
                                className="mt-3 text-sm underline hover:text-gray-600"
                                style={{ color: '#69BE28' }}
                            >
                                Create the first one
                            </button>
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Organisation</th>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Consent</th>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {organisations.filter(o => !orgSearch || o.name.toLowerCase().includes(orgSearch.toLowerCase()) || o.contactEmail.toLowerCase().includes(orgSearch.toLowerCase())).map(org => (
                                    <tr
                                        key={org.id}
                                        onClick={() => setSelectedOrg(org)}
                                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    >
                                        <td className="px-6 py-4">
                                            <p className="font-medium text-gray-900">{org.name}</p>
                                            {org.subtype && org.subtype !== 'general' && (
                                                <p className="text-xs text-gray-400 capitalize mt-0.5">{org.subtype}</p>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <OrgTypeBadge type={org.type} />
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{org.contactEmail}</td>
                                        <td className="px-6 py-4 text-gray-600 capitalize">{org.consentMethod}</td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${org.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {org.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-500">
                                            {new Date(org.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>}

            </main>

            <CreateOrgModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onOrgCreated={handleOrgCreated}
            />

            <OrgDetailModal
                isOpen={selectedOrg !== null}
                onClose={() => setSelectedOrg(null)}
                org={selectedOrg}
                onOrgUpdated={handleOrgUpdated}
                onOrgDeleted={handleOrgDeleted}
            />
        </div>
    );
};

export default SuperAdminPage;
